/**
 * Cached-user helpers — last-resort offline fallback for the signed-in user.
 *
 * Persists a trimmed copy of the Supabase user in localStorage so that auth can
 * resolve without a network call when `getSession()` would hang offline (token
 * refresh). Shared by `AuthProvider` (React tree) and `requireAuth` (router
 * `beforeLoad`) so both gate on the same source of truth.
 */
import type { User } from '@supabase/supabase-js'

export const CACHED_USER_KEY = 'imki-cached-user'

export function cacheUserLocally(user: User, role: string | null): void {
  try {
    localStorage.setItem(CACHED_USER_KEY, JSON.stringify({
      id: user.id,
      email: user.email,
      role,
      company: user.user_metadata?.company,
      user_metadata: user.user_metadata,
      cachedAt: Date.now(),
    }))
  } catch { /* localStorage unavailable */ }
}

export function getCachedUser(): { user: User; role: string | null } | null {
  try {
    const cached = localStorage.getItem(CACHED_USER_KEY)
    if (!cached) return null
    const data = JSON.parse(cached)
    // Cache valid for 7 days — aligns with typical Supabase refresh token expiry.
    // This is a last-resort fallback for offline access when even getSession() has no data.
    if (Date.now() - data.cachedAt > 7 * 24 * 60 * 60 * 1000) {
      localStorage.removeItem(CACHED_USER_KEY)
      return null
    }
    return { user: data as User, role: data.role || null }
  } catch {
    return null
  }
}

export function clearCachedUser(): void {
  try { localStorage.removeItem(CACHED_USER_KEY) } catch { /* noop */ }
}
